/**
 * Pull the most useful human-readable error string out of an RTK Query
 * rejection. Handles:
 *
 *  - Laravel validation responses: { message, errors: { field: [msg…] } }
 *    → flatten the per-field messages and show them
 *  - Plain abort responses: { message: "..." }
 *  - Network errors: { error: "TypeError: …", status: "FETCH_ERROR" }
 *  - Unparseable bodies: fall back to whatever we can stringify
 *
 * Always returns a non-empty string. The caller passes a `fallback` to
 * use when the rejection genuinely carries no useful detail.
 */
export function extractBackendError(err: unknown, fallback: string): string {
  if (!err) return fallback;
  const e = err as any;

  // Laravel 422 — { message, errors: { field: [msg, …] } }
  const validation = e?.data?.errors;
  if (validation && typeof validation === "object") {
    const lines: string[] = [];
    for (const field of Object.keys(validation)) {
      const msgs = validation[field];
      if (Array.isArray(msgs) && msgs.length > 0) {
        lines.push(`${field}: ${msgs[0]}`);
      }
    }
    if (lines.length > 0) return lines.join(" · ");
  }

  // Generic Laravel message
  const msg =
    e?.data?.message ||
    e?.error ||
    e?.message ||
    (typeof e?.data === "string" ? e.data : null);

  if (typeof msg === "string" && msg.trim().length > 0) return msg;

  return fallback;
}
